/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { TenantAccount } from '../../gql-types.generated';
import { RootState } from '../../store';

interface SliceState {
  tenantAccount?: TenantAccount;
  loading: boolean;
  error?: Error;
  updateLoading: boolean;
  updateError?: Error;
  updateSuccess: boolean;
  updatePaymentTypesLoading: boolean;
  updatePaymentTypesError?: Error;
  updatePaymentTypesSuccess: boolean;
  updateFeatureFlagsLoading: boolean;
  updateFeatureFlagsError?: Error;
  updateFeatureFlagsSuccess: boolean;
}

const initialState: SliceState = {
  tenantAccount: undefined,
  loading: false,
  error: undefined,
  updateLoading: false,
  updateError: undefined,
  updateSuccess: false,
  updatePaymentTypesLoading: false,
  updatePaymentTypesError: undefined,
  updatePaymentTypesSuccess: false,
  updateFeatureFlagsLoading: false,
  updateFeatureFlagsError: undefined,
  updateFeatureFlagsSuccess: false,
};

export const slice = createSlice({
  name: 'tenantSettings',
  initialState,
  reducers: {
    fetchTenantStart: state => {
      state.loading = true;
      state.error = undefined;
    },
    fetchTenantSuccess: (state, action: PayloadAction<TenantAccount>) => {
      state.tenantAccount = action.payload;
      state.loading = false;
      state.error = undefined;
    },
    fetchTenantError: (state, action: PayloadAction<Error>) => {
      state.error = action.payload;
      state.loading = false;
    },
    updateTenantStart: state => {
      state.updateLoading = true;
      state.updateError = undefined;
      state.updateSuccess = false;
    },
    updateTenantSuccess: state => {
      state.updateLoading = false;
      state.updateSuccess = true;
      state.updateError = undefined;
    },
    updateTenantError: (state, action: PayloadAction<Error>) => {
      state.updateError = action.payload;
      state.updateLoading = false;
      state.updateSuccess = false;
    },
    clearUpdateStatus: state => {
      state.updateError = undefined;
      state.updateSuccess = false;
    },
    updatePaymentTypesStart: state => {
      state.updatePaymentTypesLoading = true;
      state.updatePaymentTypesError = undefined;
      state.updatePaymentTypesSuccess = false;
    },
    updatePaymentTypesSuccess: state => {
      state.updatePaymentTypesLoading = false;
      state.updatePaymentTypesSuccess = true;
      state.updatePaymentTypesError = undefined;
    },
    updatePaymentTypesError: (state, action: PayloadAction<Error>) => {
      state.updatePaymentTypesError = action.payload;
      state.updatePaymentTypesLoading = false;
      state.updatePaymentTypesSuccess = false;
    },
    clearPaymentTypesUpdateStatus: state => {
      state.updatePaymentTypesError = undefined;
      state.updatePaymentTypesSuccess = false;
    },
    updateFeatureFlagsStart: state => {
      state.updateFeatureFlagsLoading = true;
      state.updateFeatureFlagsError = undefined;
      state.updateFeatureFlagsSuccess = false;
    },
    updateFeatureFlagsSuccess: state => {
      state.updateFeatureFlagsLoading = false;
      state.updateFeatureFlagsSuccess = true;
      state.updateFeatureFlagsError = undefined;
    },
    updateFeatureFlagsError: (state, action: PayloadAction<Error>) => {
      state.updateFeatureFlagsError = action.payload;
      state.updateFeatureFlagsLoading = false;
      state.updateFeatureFlagsSuccess = false;
    },
    clearFeatureFlagsUpdateStatus: state => {
      state.updateFeatureFlagsError = undefined;
      state.updateFeatureFlagsSuccess = false;
    },
    clearTenantSettings: () => initialState,
  },
});

export const selectTenantSettingsAccount = (state: RootState): TenantAccount | undefined => state.tenantSettings.tenantAccount;
export const selectTenantSettingsLoading = (state: RootState): boolean => state.tenantSettings.loading;
export const selectTenantSettingsError = (state: RootState): Error | undefined => state.tenantSettings.error;
export const selectTenantSettingsUpdateLoading = (state: RootState): boolean => state.tenantSettings.updateLoading;
export const selectTenantSettingsUpdateError = (state: RootState): Error | undefined => state.tenantSettings.updateError;
export const selectTenantSettingsUpdateSuccess = (state: RootState): boolean => state.tenantSettings.updateSuccess;
export const selectTenantSettingsUpdatePaymentTypesLoading = (state: RootState): boolean =>
  state.tenantSettings.updatePaymentTypesLoading;
export const selectTenantSettingsUpdatePaymentTypesError = (state: RootState): Error | undefined =>
  state.tenantSettings.updatePaymentTypesError;
export const selectTenantSettingsUpdatePaymentTypesSuccess = (state: RootState): boolean =>
  state.tenantSettings.updatePaymentTypesSuccess;
export const selectTenantSettingsUpdateFeatureFlagsLoading = (state: RootState): boolean =>
  state.tenantSettings.updateFeatureFlagsLoading;
export const selectTenantSettingsUpdateFeatureFlagsError = (state: RootState): Error | undefined =>
  state.tenantSettings.updateFeatureFlagsError;
export const selectTenantSettingsUpdateFeatureFlagsSuccess = (state: RootState): boolean =>
  state.tenantSettings.updateFeatureFlagsSuccess;

export const {
  fetchTenantStart,
  fetchTenantSuccess,
  fetchTenantError,
  updateTenantStart,
  updateTenantSuccess,
  updateTenantError,
  clearUpdateStatus,
  updatePaymentTypesStart,
  updatePaymentTypesSuccess,
  updatePaymentTypesError,
  clearPaymentTypesUpdateStatus,
  updateFeatureFlagsStart,
  updateFeatureFlagsSuccess,
  updateFeatureFlagsError,
  clearFeatureFlagsUpdateStatus,
  clearTenantSettings,
} = slice.actions;

export default slice.reducer;
